<template>
  <div :class="[classes.listCardWrapper, 'card']">
    <div :class="classes.colOne">
      <CardHeader :class="classes.datesport" :eventId="eventId" />

      <EventNames
        :class="classes.eventNames"
        :eventId="eventId"
        :marketId="marketId"
      />
    </div>

    <div :class="classes.colTwo">
      <MarketName
        :marketId="market.marketId"
        :fallbackName="market.marketName"
        :class="[classes.marketName, 'market-name']"
      />

      <ScrollContainer
        :elementHeight="27"
        :gap="3"
        :pb="5"
        :colWidth="0"
        @scroll.once="handleScroll"
      >
        <ListOption
          v-for="(option, i) in options"
          :key="option.id"
          :stagger="i"
          :eventId="eventId"
          :option="option"
          :marketId="market.marketId"
        />
      </ScrollContainer>
    </div>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/vue-jss/src";
import { defineComponent, reactive, computed, ComputedRef } from "vue";
import {
  BrandContext,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";

import ListOption from "../ListOption.vue";
import EventNames from "../../../EventNamesBadgesStacked.vue";
import MarketName from "../../../MarketName.vue";
import CardHeader from "../../../CardHeaderLeaderboard.vue";
import ScrollContainer from "../ScrollContainer.vue";

import {
  composeMarket,
  composeListMarketOptions,
  composeEvent
} from "@fresh8/ad-data-composition/src/betData";
import { Brand } from "@fresh8/types/src";
import composeDate from "@fresh8/ad-data-composition/src/date";
import { injectCarouselContext } from "../../../../lib/provideInjectors";
import { Market, SportEvent, Option } from "@fresh8/types/src/betData";
import { useHandleAnalytics } from "../../../../lib/analytics";
import { useHandleCardAnalytics } from "../../../../lib/card-analytics";
import { AnalyticEvents } from "@fresh8/types/src/analytics";
import { injectCardNumber } from "../../../../hooks/useAnalytics";

const useStyles = createUseStyles({
  cardHeaderWrapper: {
    display: "flex",
    position: "relative",
    width: "100%",
    boxSizing: "border-box",
    alignItems: "center"
  },
  datesport: {
    "& span": {
      fontSize: "10px !important"
    }
  },
  marketName: {
    height: "18px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "end",
    paddingRight: "8px"
  },
  listCardWrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    height: "100%",
    position: "relative",
    boxSizing: "border-box",
    background: props => props.cardBackgroundColor,
    border: props => `1px solid ${props.marketBorderColor || "#dcddf"}`,
    borderRadius: props => `${props.borderRadius}px`
  },
  eventNames: {
    paddingTop: "5px",
    paddingBottom: "10px !important"
  },
  colOne: {
    display: "flex",
    flexDirection: "column",
    borderRight: props => `1px solid ${props.marketBorderColor || "#dcddf"}`
  },
  colTwo: {
    display: "flex",
    flexDirection: "column",
    flex: 1
  }
});

export default defineComponent({
  name: "SmallResponsive",
  components: {
    ListOption,
    CardHeader,
    EventNames,
    MarketName,
    ScrollContainer
  },
  props: {
    marketId: { type: String, required: true },
    eventId: { type: String, required: true }
  },
  setup(props) {
    const {
      adTemplateBranding,
      marketBranding,
      backgroundImageSets
    } = injectBrand() as BrandContext;
    const $handleAnalytics = useHandleAnalytics();
    const $handleCardAnalytics = useHandleCardAnalytics();
    const cardNumber = injectCardNumber();

    const backgroundImage = computed(() => {
      return !!Object.keys(backgroundImageSets?.value || {}).length;
    });

    const carouselContext = injectCarouselContext();

    const relativeStyles = computed(() => ({
      ...adTemplateBranding.value,
      backgroundImage: backgroundImage.value,
      isCarousel: carouselContext.value.isCarousel
    }));

    const classesRef = useStyles(relativeStyles);
    const market = composeMarket(props.marketId) as ComputedRef<Market>;
    const marketOptions = composeListMarketOptions(
      props.marketId
    ) as ComputedRef<Option[]>;
    const event = composeEvent(props.eventId) as ComputedRef<SportEvent>;
    const fixtureStartDate = composeDate(event.value?.id);

    const listWrapperStyles = reactive({
      height: "0"
    });

    const eventNameStyles = reactive({
      height: "0"
    });

    const handleScroll = () => {
      $handleAnalytics(AnalyticEvents.INTERACTED, "List Scroll");
      $handleCardAnalytics(
        AnalyticEvents.INTERACTED,
        "List Scroll",
        undefined,
        cardNumber
      );
    };

    return {
      handleScroll,
      eventNameStyles,
      listWrapperStyles,
      market,
      marketBranding,
      classes: classesRef,
      options: marketOptions,
      event,
      fixtureStartDate,
      marketNameStyle: {
        get(brand: Brand) {
          return brand.adTemplate.market.marketNameFont;
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.marketNameFont = value;
        }
      },
      fixtureTimeFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureTimeFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureTimeFont = value;
        }
      },
      fixtureSportFont: {
        get(brand: Brand) {
          return (
            brand.adTemplate.market.fixtureSportFont ||
            Object.keys(brand.fonts)[0]
          );
        },
        set(brand: Brand, value: string) {
          brand.adTemplate.market.fixtureSportFont = value;
        }
      },
      fixtureNameStyle: {
        get: (brand: Brand) => brand.adTemplate.market.teamNameFont,
        set: (brand: Brand, value: string) =>
          (brand.adTemplate.market.teamNameFont = value)
      }
    };
  }
});
</script>
